import { useEffect, useRef, useState } from 'react'
import { api, apiUrl } from '../api/client'
import { asksFor } from '../completeness'
import { SettingRow } from '../components/SettingRow'
import { Sheet } from '../components/Sheet'
import { EmailCapture } from '../components/EmailCapture'
import { Fold } from '../components/Fold'

/*
  UI SPEC §11 territory, but the substance is from the build spec: everything on this
  screen lands in the cached prefix (§5.1) and is read by every member on every call.

  So the screen leads with what is missing rather than presenting a neutral form. A board
  that does not know EBITDA gives thin advice, and the owner should be able to see why.

  ── Three things this screen got wrong, and it is one of the first anybody opens ──────

  **The headings were quieter than their own contents.** Every group was a `.card__title`:
  15px, weight 500, --text-secondary. The row values under them are 15px navy. So each
  heading was set smaller and lighter than the rows it was meant to introduce.
  `.section-title` already exists for exactly this level and The Numbers already used it.

  **The gaps were a list, not an argument.** The screen opened by naming what it did not
  know, comma-joined, under one generic reason. `completeness.ts` has carried a ranked list
  of the same gaps the whole time, each with its own sentence saying what filling it buys,
  and none of it reached this screen. It does now: one ask at a time, most valuable first,
  which is the rule that file was written around.

  **It asserted the payoff and never showed it.** The standfirst says the board reads this
  before every meeting. `BoardReads` is that sentence, composed out of the same fields, with
  every gap left as a blank you can press. Nothing in it is generated or fetched; it is the
  profile, written out the way the board receives it.

  No gold anywhere on this screen. `.w--meeting` is the only place in the product where navy
  and gold appear together at full strength, and that is what makes the next meeting read as
  the anchor of Home rather than as one more card. A second navy-and-gold hero spends it.
*/

interface CompanyDoc {
  id: string
  kind: string
  kindLabel: string
  periodLabel: string | null
  fileName: string | null
  sizeBytes: number
  uploadedAt: string
  readable: boolean
  extractionStatus: 'none' | 'pending' | 'done' | 'scanned' | 'truncated' | 'failed'
  pageCount: number | null
  extractedChars: number
}

interface Company {
  name: string
  trade: string
  timezone: string
  headcount: number | null
  annualRevenue: number | null
  ebitda: number | null
  ebitdaMargin: number | null
  fiscalYearEnd: string | null
  website: string | null
  gbpUrl: string | null
  founderSummary: string | null
  meetingDow: number
  meetingHour: number
  completeness: number
  missing: string[]
  documents: CompanyDoc[]
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/*
  Seven in the morning to seven at night, written the way a tradesman says it.

  The picker used to offer all 24 hours as 00:00 to 23:00. Both halves of that were wrong.
  Nobody schedules their board for 3am, so twenty-four options made the owner read past
  seventeen he would never pick to reach the four he might. And 07:00 is how a duty roster
  writes it, not how anybody says it out loud.

  The column still takes 0-23 and the API still accepts it. This is the list of hours worth
  offering, not a new rule about what the column may hold: an account already set outside
  this range keeps its hour until somebody deliberately changes it.
*/
const MEETING_HOURS = Array.from({ length: 13 }, (_, i) => {
  const h = i + 7
  const suffix = h < 12 ? 'AM' : 'PM'
  const shown = h % 12 === 0 ? 12 : h % 12
  return { value: h, label: `${shown}:00 ${suffix}` }
})

export function CompanySettings({ onSaved }: { onSaved: () => void }) {
  const [company, setCompany] = useState<Company | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = async () => setCompany(await api.get<Company>('/api/company'))

  useEffect(() => { void load() }, [])

  if (!company) return <div className="skeleton" style={{ width: '50%' }} />

  /*
    One field at a time, committed the moment the owner is done with it.

    The PUT takes the whole profile, so each row sends the current company plus its own
    change. That is deliberate: a screen with a Save button at the bottom means an owner
    who edits one number and navigates away loses it, and on this screen a lost number is
    a board reasoning from a figure that is no longer true.
  */
  const saveField = async <K extends keyof Company>(key: K, value: Company[K]) => {
    setError(null)
    try {
      await api.put<void>('/api/company', {
        name: company.name,
        trade: company.trade,
        timezone: company.timezone,
        headcount: company.headcount,
        annualRevenue: company.annualRevenue,
        ebitda: company.ebitda,
        fiscalYearEnd: company.fiscalYearEnd || null,
        website: company.website,
        gbpUrl: company.gbpUrl,
        founderSummary: company.founderSummary,
        meetingDow: company.meetingDow,
        meetingHour: company.meetingHour,
        [key]: value,
      })
      await load()
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save')
      throw e
    }
  }

  const filled = (v: unknown) => v !== null && v !== undefined && v !== ''
  const basics = [company.name, company.trade, company.headcount, company.annualRevenue,
                  company.ebitda, company.fiscalYearEnd].filter(filled).length
  const words = [company.founderSummary, company.website, company.gbpUrl].filter(filled).length

  return (
    <>
      <h1 className="screen-title">Your company</h1>
      <p className="screen-sub">
        The board reads this before every meeting. What is missing here is what it has to guess at.
      </p>

      <ProfilePanel company={company} />

      {error && <div className="signin__error">{error}</div>}

      {/* The profile panel stays full width above this: it is the one thing on the screen
          that is about the screen as a whole. Everything under it is a section that stands
          on its own, so above 1080px they sit two abreast and the rows keep their length.
          Each cell carries its own heading, because a heading stranded in the opposite
          column from its card is worse than a narrow page. */}
      <div className="colstack">

      {/*
        Folded once it is complete, open while it is not.

        Six filled fields the owner will not touch again should not stand between them and
        Statements, which is the part of this screen with something to do on it. An
        incomplete section stays open, because a fold is a good way to hide a question
        nobody has answered.
      */}
      <FoldSection title="The basics" done={basics} total={6}>

      <div className="card card--rows">
        <SettingRow id="f-name" markEmpty label="Company name" value={company.name}
                    onSave={v => saveField('name', v as string)} />

        <SettingRow id="f-trade" markEmpty label="Trade" value={company.trade} placeholder="plumbing"
                    onSave={v => saveField('trade', v as string)} />

        <SettingRow id="f-headcount" markEmpty label="Employees" value={company.headcount} kind="number"
                    onSave={v => saveField('headcount', v as number | null)} />

        <SettingRow id="f-revenue" markEmpty label="Annual revenue" value={company.annualRevenue} kind="money"
                    hint="Last full year, in dollars"
                    onSave={v => saveField('annualRevenue', v as number | null)} />

        <SettingRow id="f-ebitda" markEmpty label="EBITDA" value={company.ebitda} kind="money"
                    hint="Negative is fine and worth saying"
                    onSave={v => saveField('ebitda', v as number | null)} />

        {company.ebitdaMargin !== null && (
          <div className="row row--derived">
            <span className="row__label">Margin</span>
            <span className="row__value">{(company.ebitdaMargin * 100).toFixed(1)}%</span>
          </div>
        )}

        <SettingRow id="f-fye" markEmpty label="Fiscal year ends" value={company.fiscalYearEnd} kind="date"
                    onSave={v => saveField('fiscalYearEnd', v as string | null)} />
      </div>

      </FoldSection>

      <FoldSection title="In your words" done={words} total={3}>

      <div className="card card--rows">
        <SettingRow id="f-summary" markEmpty label="What the business does" value={company.founderSummary} kind="textarea"
                    hint="Who you serve, what you sell, what you are trying to build. Two or three sentences beats a paragraph of marketing."
                    onSave={v => saveField('founderSummary', v as string | null)} />

        <SettingRow id="f-website" markEmpty label="Website" value={company.website} kind="url" placeholder="https://"
                    onSave={v => saveField('website', v as string | null)} />

        <SettingRow id="f-gbp" markEmpty label="Google Business Profile" value={company.gbpUrl} kind="url"
                    placeholder="https://" hint="Your reviews page"
                    onSave={v => saveField('gbpUrl', v as string | null)} />
      </div>

      </FoldSection>

      <section>
        <SectionHead title="What the board reads" />
        <BoardReads company={company} />
      </section>

      <section>
        <SectionHead title="When the board meets" />

      <div className="card card--rows">
        <SettingRow label="Day" value={company.meetingDow} kind="select"
                    options={DAYS.map((d, i) => ({ value: i + 1, label: d }))}
                    onSave={v => saveField('meetingDow', v as number)} />

        <SettingRow label="Hour" value={company.meetingHour} kind="select" hint="Your local time"
                    options={MEETING_HOURS}
                    onSave={v => saveField('meetingHour', v as number)} />
        </div>
      </section>

      <section>
        <SectionHead title="Statements" />
        <Statements documents={company.documents} onChanged={load} />
      </section>

      <section>
        <EmailCapture />
      </section>

      </div>
    </>
  )
}



/* ── Section headings ───────────────────────────────────────────────────── */
/*
  The count is deliberately not the same unit as the profile panel's percentage. The panel
  measures the eight things `completeness.ts` calls essential; a section counts its own
  rows. Two counts in the same unit that did not add up to each other is the kind of thing
  somebody notices once and never trusts again, so they are kept plainly different.
*/
/*
  A counted section that folds shut once its rows are all filled.

  The count is the fold's own meta line rather than a second element beside the heading, so
  a closed section still says why it is closed.
*/
function FoldSection({
  title, done, total, children,
}: { title: string; done: number; total: number; children: React.ReactNode }) {
  const complete = done === total
  return (
    <Fold
      title={title}
      meta={complete ? 'All in' : `${done} of ${total}`}
      defaultOpen={!complete}
    >
      {children}
    </Fold>
  )
}

function SectionHead({ title, done, total }: { title: string; done?: number; total?: number }) {
  const complete = done !== undefined && done === total
  return (
    <div className="sec">
      <h2 className="section-title">{title}</h2>
      {done !== undefined && total !== undefined && (
        <span className={`sec__count${complete ? ' is-done' : ''}`}>
          {complete ? 'All in' : `${done} of ${total}`}
        </span>
      )}
    </div>
  )
}

/* ── The profile panel ──────────────────────────────────────────────────── */
/*
  What replaced the amber "the board does not know your EBITDA, headcount, website".

  That card had the shape of a warning and the content of a list. It named every gap at
  once, which `completeness.ts` had already decided was the wrong move (a list of eight
  missing fields is a chore and gets ignored), and it attached one generic reason to all of
  them rather than the specific one each gap already carries.

  So this asks for one thing, the most valuable outstanding one, in that file's own words.
  The segments are the eight essentials and the percentage is the server's own number, so
  the owner sees the whole distance and the next step without reading a list.

  Navy rather than amber because an incomplete profile is not an error. It is a new
  account, which is the normal state of this screen in the first week somebody owns it.
*/
function ProfilePanel({ company }: { company: Company }) {
  const asks = asksFor({
    headcount: company.headcount,
    annualRevenue: company.annualRevenue,
    ebitda: company.ebitda,
    founderSummary: company.founderSummary,
    website: company.website,
    gbpUrl: company.gbpUrl,
    fiscalYearEnd: company.fiscalYearEnd,
    trade: company.trade,
    completeness: company.completeness,
  })

  const next = asks[0] ?? null
  const SEGMENTS = 8
  const lit = Math.round((company.completeness / 100) * SEGMENTS)

  return (
    <div className={`profile${next ? '' : ' profile--done'}`}>
      <div className="profile__head">
        <span className="profile__label">What the board knows</span>
        <span className="profile__pct">{company.completeness}%</span>
      </div>

      <div className="profile__bar" aria-hidden>
        {Array.from({ length: SEGMENTS }, (_, i) => (
          <span key={i} className={`profile__seg${i < lit ? ' is-filled' : ''}`} />
        ))}
      </div>

      {next ? (
        <div className="profile__ask">
          <div className="profile__ask-body">
            <div className="profile__ask-label">{next.label}</div>
            <p className="profile__ask-why">{next.why}</p>
          </div>
          {/*
            The ask that wants a document sends them to the drop zone at the foot of this
            screen; every other one opens the row it is about. Both are on this page, so
            neither is a navigation.
          */}
          <button className="profile__go" onClick={() => openField(FIELD_FOR[next.id] ?? 'drop')}>
            {next.cta}
          </button>
        </div>
      ) : (
        <p className="profile__done">
          The board has everything it asked for. Anything you add from here sharpens the
          advice rather than filling a hole.
        </p>
      )}
    </div>
  )
}

/** Which row on this screen each outstanding ask is about. */
const FIELD_FOR: Record<string, string> = {
  summary: 'f-summary',
  revenue: 'f-revenue',
  headcount: 'f-headcount',
  ebitda: 'f-ebitda',
  statement: 'drop',
}

/*
  Open a row from somewhere else on the screen.

  SettingRow keeps its own editing state, which is right: nothing else needs to know
  whether a row is open. Rather than lift that into this screen so two callers can reach
  it, the row carries an id and this presses it. The row is already a button, so pressing
  it is exactly what a person does to open it.
*/
function openField(id: string) {
  const el = document.getElementById(id)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  // After the scroll, or the row opens and the focused input yanks the page back.
  setTimeout(() => el.click(), 260)
}

/* ── What the board reads ───────────────────────────────────────────────── */
/*
  The payoff, and the reason the rest of the screen is worth filling in.

  Everything above this asserts that the board reads the profile. This is the profile,
  written out as the sentences it is handed, with every gap left as a blank that opens the
  row it belongs to. It is composed here from the same fields rather than fetched, so it
  cannot drift out of step with the rows above it and it costs no call.

  It is not the real cached prefix and does not pretend to be: no figures from The Numbers,
  no meeting history, no seat instructions. It is the part of the prefix this screen owns,
  which is the part the owner can do something about.
*/
function BoardReads({ company }: { company: Company }) {
  const blank = (label: string, field: string) => (
    <button className="reads__blank" onClick={() => openField(field)}>{label}</button>
  )

  const name = company.name?.trim() || 'This business'
  const trade = company.trade?.trim()

  // Telling somebody to press a blank when the paragraph has none reads as a screen that
  // has not noticed it is finished.
  const anyBlanks = !trade
    || company.annualRevenue === null
    || company.headcount === null || company.headcount === 0
    || company.ebitda === null
    || !company.fiscalYearEnd
    || !company.founderSummary?.trim()

  return (
    <div className="reads">
      <p className="reads__body">
        <strong>{name}</strong> is {trade ? `a ${trade} business` : blank('what trade', 'f-trade')}
        {' '}turning over{' '}
        {company.annualRevenue !== null
          ? <strong>{money(company.annualRevenue)}</strong>
          : blank('revenue', 'f-revenue')}
        {' '}a year with{' '}
        {company.headcount !== null && company.headcount > 0
          ? <strong>{company.headcount}</strong>
          : blank('how many', 'f-headcount')}
        {' '}on the books. It earns{' '}
        {company.ebitda !== null
          ? <strong>{money(company.ebitda)}</strong>
          : blank('EBITDA', 'f-ebitda')}
        {' '}before financing
        {company.fiscalYearEnd
          ? <>, and its year ends <strong>{shortDate(company.fiscalYearEnd)}</strong>.</>
          : <>, and its year ends {blank('when', 'f-fye')}.</>}
      </p>

      {company.founderSummary?.trim() ? (
        <p className="reads__own">&ldquo;{company.founderSummary.trim()}&rdquo;</p>
      ) : (
        <p className="reads__own reads__own--empty">
          In your own words: {blank('what the business does', 'f-summary')}
        </p>
      )}

      <div className="reads__foot">
        Every seat is handed this before it says a word.
        {anyBlanks && ' Press a blank to fill it.'}
      </div>
    </div>
  )
}

/** Short enough to sit inside a sentence: $2.4m, $840k, $12,000. */
function money(n: number): string {
  const abs = Math.abs(n)
  const sign = n < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}m`
  if (abs >= 10_000) return `${sign}$${Math.round(abs / 1000)}k`
  return `${sign}$${abs.toLocaleString('en-US')}`
}

/*
  A fiscal year end is a calendar day, so it is parsed as one. new Date("2026-12-31") is
  midnight UTC and prints as the thirtieth anywhere west of Greenwich, which is the bug
  src/dates.ts exists to stop.
*/
function shortDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
}

const SCAN_PAGE_LIMIT = 5   // PdfPageImages.MaxPages

/*
  Handing the board a statement.

  This used to be the only route a document could take into the product, so it carried
  everything at once: a period field, a paste box, a drop zone, a list of every file the
  business had ever uploaded, and five lines of caveats about formats — all on screen
  before you had chosen anything. Two of those jobs now belong to screens that do them
  properly.

  The File Cabinet lists every file with a sentence saying what is in it, filters and an
  open link. The list here was a worse copy of that under a heading reading "Profit and
  loss" while showing screenshots dropped on The Table. It is gone, and Remove went with
  it, to the Cabinet, where the list is.

  The Numbers reconciles every figure from every source and now names the ones a board in
  this trade actually wants. So "paste the key lines: revenue, cost of goods, gross profit,
  payroll" was asking for a worse version of a screen that asks better. Pasting survives as
  the recovery path for a scan nothing can read, behind a link, not as a standing textarea.

  What is left is the one thing this screen is for: drop a statement, and be told where the
  file went and where its figures went.
*/
function Statements({ documents, onChanged }: { documents: CompanyDoc[]; onChanged: () => Promise<void> }) {
  const [file, setFile] = useState<File | null>(null)
  const [period, setPeriod] = useState('')
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pasting, setPasting] = useState(false)
  const [addedId, setAddedId] = useState<string | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  /*
    Extraction finishes in the worker a second or two after the upload returns, so the row
    lands saying "reading it now" and has to correct itself. Without this the owner saw a
    file that claimed the board could not read it, which was true for about two seconds
    and alarming for as long as they left the page open.
  */
  useEffect(() => {
    if (!documents.some(d => d.extractionStatus === 'pending')) return
    const id = setTimeout(() => { void onChanged() }, 2500)
    return () => clearTimeout(id)
  }, [documents, onChanged])

  const send = async (body: FormData): Promise<string | null> => {
    const response = await fetch(apiUrl('/api/company/documents'), {
      method: 'POST', body, credentials: 'include',
    })
    if (!response.ok) {
      const detail = await response.json().catch(() => null)
      throw new Error(detail?.error ?? 'Upload failed')
    }
    const created = await response.json().catch(() => null)
    return created?.id ?? null
  }

  const upload = async () => {
    if (!file) return
    setBusy(true)
    setError(null)

    const body = new FormData()
    body.append('kind', 'profit_loss')
    if (period.trim()) body.append('periodLabel', period.trim())
    body.append('file', file)

    try {
      setAddedId(await send(body))
      setFile(null); setPeriod('')
      await onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  // Only the one just added. Everything else is the File Cabinet's job, and repeating the
  // whole list here is what made this card six hundred pixels of duplicate.
  const added = addedId ? documents.find(d => d.id === addedId) ?? null : null

  return (
    <div className="card">
      <div className="card__title" style={{ marginBottom: 'var(--space-2)' }}>Statements</div>
      <p className="muted" style={{ marginBottom: 'var(--space-4)' }}>
        A profit and loss, a balance sheet, a bank statement. The board reads the figures
        out of it and they land in <a className="inline-link" href="#numbers">The Numbers</a>,
        where you can correct anything it got wrong.
      </p>

      {error && <div className="signin__error" style={{ marginBottom: 'var(--space-3)' }}>{error}</div>}

      {file ? (
        /*
          The period only appears once there is a file to attach it to. An empty "FY2025"
          box on arrival is a question about nothing, and it was the first thing on the
          card. Optional, because the statement usually says so itself and the extraction
          reads it; worth asking, because when it does not, an undated figure groups with
          nothing and sits in The Numbers under "Other periods".
        */
        <div className="upload">
          <div className="upload__file">
            <span className="upload__name">{file.name}</span>
            <span className="dim">{Math.round(file.size / 1024)} KB</span>
          </div>

          <label className="field">
            <span className="field__label">Period it covers</span>
            <input className="input" value={period} placeholder="FY2025"
                   onChange={e => setPeriod(e.target.value)} />
            <span className="field__hint">Optional. Skip it if the statement says so itself.</span>
          </label>

          <div className="sheet__actions">
            <button className="btn" onClick={() => { setFile(null); setPeriod('') }}>Choose another</button>
            <button className="btn btn--brand" disabled={busy} onClick={upload}>
              {busy ? 'Sending' : 'Hand it to the board'}
            </button>
          </div>
        </div>
      ) : (
        <div
          id="drop"
          className={`drop ${dragging ? 'is-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault()
            setDragging(false)
            const dropped = e.dataTransfer.files?.[0]
            if (dropped) { setFile(dropped); setAddedId(null) }
          }}
          onClick={() => picker.current?.click()}
        >
          <input
            ref={picker}
            type="file"
            hidden
            accept=".pdf,.csv,.txt,.xlsx,.xlsm,.xls,.png,.jpg,.jpeg,.gif,.webp"
            onChange={e => {
              setFile(e.target.files?.[0] ?? null); setAddedId(null); e.target.value = ''
            }}
          />
          <span>Drop a statement here, or click to choose a file</span>
          <span className="dim">
            PDFs, spreadsheets, screenshots and photographs of paper all get read
          </span>
        </div>
      )}

      {added && (
        <p className="upload__done">
          <strong>{added.fileName ?? 'Your figures'}</strong>{' '}
          {added.extractionStatus === 'pending'
            ? 'is being read now. Its figures will be in The Numbers in a moment.'
            : added.readable
              ? 'is filed. Its figures are in The Numbers and the file is in the File Cabinet.'
              : 'is filed, but nothing readable came out of it. Paste the key lines and the board still gets them.'}
        </p>
      )}

      {/*
        The caveats, folded away. Every line of this is worth having and none of it is worth
        reading before you have chosen a file: it is five lines of things that might go
        wrong, and it used to sit between the drop zone and the button.
      */}
      <details className="disclose">
        <summary className="disclose__head">What can it read?</summary>
        <div className="disclose__body">
          <p>
            A PDF with real text is read exactly. A scan, or a photograph of one, is read by
            eye instead — the first {SCAN_PAGE_LIMIT} pages of it, because every page is a
            separate look.
          </p>
          <p>
            Photographs have to be JPEG, PNG, GIF or WebP and under 5 MB, so an iPhone HEIC
            needs saving as a JPEG first. Spreadsheets and CSVs are read as text. Anything
            up to 25 MB.
          </p>
        </div>
      </details>

      <p className="dim upload__foot">
        {documents.length > 0 && (
          <>
            <a className="inline-link" href="#cabinet">
              {documents.length} {documents.length === 1 ? 'file' : 'files'} in the File Cabinet
            </a>
            {' \u00b7 '}
          </>
        )}
        <a className="inline-link" href="#numbers">Know the figures already? Add them in The Numbers</a>
        {' \u00b7 '}
        <button className="linkish" onClick={() => setPasting(true)}>Paste the key lines</button>
      </p>

      {pasting && (
        <PasteFigures
          onClose={() => setPasting(false)}
          onSent={async id => { setPasting(false); setAddedId(id); await onChanged() }}
          send={send}
        />
      )}
    </div>
  )
}

/*
  The fallback, for a statement nothing can read.

  A fax-quality scan, a CCITT page, a photograph too dark to transcribe. The board will not
  get those figures any other way, and typing six lines here beats retyping the statement
  into The Numbers one field at a time. A sheet rather than a field on the page, because it
  is the exception, and the exception should not be the first thing an owner meets on a
  screen about their own company.
*/
function PasteFigures({
  onClose, onSent, send,
}: {
  onClose: () => void
  onSent: (id: string | null) => Promise<void>
  send: (body: FormData) => Promise<string | null>
}) {
  const [period, setPeriod] = useState('')
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy || !text.trim()) return
    setBusy(true); setError(null)

    const body = new FormData()
    body.append('kind', 'profit_loss')
    if (period.trim()) body.append('periodLabel', period.trim())
    body.append('extractedText', text.trim())

    try {
      await onSent(await send(body))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that')
      setBusy(false)
    }
  }

  return (
    <Sheet title="Paste the key lines" onClose={onClose}>
      <p className="muted" style={{ marginBottom: 'var(--space-4)' }}>
        For a statement nothing could read. Paste or type the lines as they appear and they
        are read the same way a file is: the figures land in The Numbers with this as their
        source.
      </p>

      <form className="pw" onSubmit={submit}>
        {error && <div className="signin__error">{error}</div>}

        <label className="field">
          <span className="field__label">Period it covers</span>
          <input className="input" value={period} placeholder="FY2025"
                 onChange={e => setPeriod(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">The lines</span>
          <textarea
            className="textarea"
            rows={8}
            value={text}
            placeholder={PASTE_EXAMPLE}
            onChange={e => setText(e.target.value)}
          />
          <span className="field__hint">
            Revenue, cost of goods, gross profit, payroll, overhead, net. Rough is fine.
          </span>
        </label>

        <div className="sheet__actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" disabled={busy || !text.trim()}>
            {busy ? 'Sending' : 'Hand it to the board'}
          </button>
        </div>
      </form>
    </Sheet>
  )
}

const PASTE_EXAMPLE = [
  'Total sales 1,697,000',
  'Cost of goods 411,000',
  'Payroll 509,100',
  'Net profit 148,600',
].join('\n')
